import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import { createPortal } from "react-dom";

export function SpeakerCard({ speaker, noModal, onClick }) {
  const [portraitSrc, setPortraitSrc] = useState(
    "/img/portraits/portrait-tba.webp",
  );

  useEffect(() => {
    if (!speaker?.name) return;
    const slug = speaker.name
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");
    // const path = `/img/portraits/portrait-${slug}${speaker.isKeynote ? "-keynote" : ""}.webp`;
    const path = `/img/portraits/portrait-${slug}.webp`;
    const img = new Image();
    img.src = path;
    img.onload = () => setPortraitSrc(path);
    img.onerror = () => {
      console.log("portrait not found:", path);
      setPortraitSrc("/img/portraits/portrait-tba.webp");
    };
  }, [speaker?.name, speaker?.isKeynote]);

  const isTba = !speaker?.name;
  const isComingSoon = speaker?.name?.startsWith("Coming Soon");
  const handleClick = () => !noModal && !isComingSoon && onClick?.(speaker);

  return (
    <div
      className={`speaker-card ${speaker?.isKeynote ? "speaker-card-keynote" : ""} ${speaker?.invisible ? "speaker-card-invisible" : ""} ${noModal ? "speaker-card-no-modal" : ""}`}
      onClick={handleClick}
      role={noModal || isComingSoon ? undefined : "button"}
      tabIndex={noModal || isComingSoon ? undefined : 0}
      onKeyDown={
        noModal || isComingSoon
          ? undefined
          : (e) => e.key === "Enter" && handleClick()
      }
    >
      <div
        className="sc-left"
        style={{ backgroundImage: "url('/img/portraits/portrait-bg.webp')" }}
      >
        {isTba ? (
          <div className="sc-tba">
            <span>Coming</span>
            <br />
            <span>Soon!</span>
          </div>
        ) : (
          <div className="sc-info">
            {speaker.isKeynote && (
              <div className="feature-tag">Keynote Speaker</div>
            )}
            {speaker.isKeynote ? (
              <span className="speaker-name speaker-name-keynote">
                {speaker.name}
              </span>
            ) : (
              <span className="speaker-name">{speaker.name}</span>
            )}
            {speaker.role && <strong>{speaker.role}</strong>}
          </div>
        )}
      </div>
      <div className="sc-right">
        <img
          className="sc-portrait"
          src={portraitSrc}
          alt={speaker?.name ?? "Speaker portrait"}
          onError={(e) => {
            e.target.src = "/img/portraits/portrait-tba.webp";
          }}
        />
      </div>
    </div>
  );
}

function speakerToUrlId(name) {
  return name
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "");
}

export default function Speakers({
  className,
  speakers,
  noModal,
  colWidth,
  filter = "all",
}) {
  const getInitialSpeaker = () => {
    if (typeof window === "undefined" || !speakers?.length) return null;
    const idParam = new URL(window.location.href).searchParams.get("id");
    if (!idParam) return null;
    return (
      speakers.find((s) => s.name && speakerToUrlId(s.name) === idParam) ?? null
    );
  };

  const [selectedSpeaker, setSelectedSpeaker] = useState(getInitialSpeaker);
  const [isVisible, setIsVisible] = useState(false);
  const [modalContainer, setModalContainer] = useState(() =>
    typeof document !== "undefined"
      ? document.getElementById("modal-container")
      : null,
  );

  // If modal-container wasn't in the DOM on first render, grab it after mount
  useEffect(() => {
    if (!modalContainer) {
      setModalContainer(document.getElementById("modal-container"));
    }
  }, [modalContainer]);

  const showSpeaker = (speaker, pushUrl = true) => {
    setIsVisible(false);
    setSelectedSpeaker(speaker);
    if (pushUrl && speaker?.name) {
      const url = new URL(window.location.href);
      url.searchParams.set("id", speakerToUrlId(speaker.name));
      window.history.pushState({}, "", url.toString());
    }
  };

  const openModal = (speaker) => showSpeaker(speaker, true);

  const closeModal = () => {
    setIsVisible(false);
    setTimeout(() => setSelectedSpeaker(null), 300);
    document.body.style.overflow = "auto";

    // Remove speaker id from URL
    const url = new URL(window.location.href);
    url.searchParams.delete("id");
    window.history.pushState({}, "", url.toString());
  };

  // Trigger fade-in after selectedSpeaker is set and the overlay is in the DOM
  useEffect(() => {
    if (!selectedSpeaker) return;
    document.body.style.overflow = "hidden";
    const raf = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(raf);
  }, [selectedSpeaker]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const url = new URL(window.location.href);
      const idParam = url.searchParams.get("id");
      if (!idParam) {
        setIsVisible(false);
        setTimeout(() => setSelectedSpeaker(null), 300);
        document.body.style.overflow = "auto";
      } else if (speakers?.length) {
        const match = speakers.find(
          (s) => s.name && speakerToUrlId(s.name) === idParam,
        );
        if (match) showSpeaker(match, false);
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speakers]);

  const visibleSpeakers = speakers.filter((s) =>
    filter === "keynote"
      ? s.isKeynote
      : filter === "non-keynote"
        ? !s.isKeynote
        : true,
  );

  // Use the first ytData entry for the session box
  const sessionData = selectedSpeaker?.ytData?.[0];

  return (
    <>
      <div className="row pb-5">
        {visibleSpeakers.map((speaker, index) => (
          <div
            key={index}
            className={`px-2 py-3 ${speaker.classes ?? ""} ${speaker.isKeynote ? "col-12 col-lg-6" : `col-6 col-lg-${colWidth || 4}`}`}
          >
            <SpeakerCard
              speaker={speaker}
              noModal={noModal}
              onClick={openModal}
            />
          </div>
        ))}
      </div>

      {selectedSpeaker &&
        modalContainer &&
        createPortal(
          <div
            className={`user-overlay ${isVisible ? "visible" : ""}`}
            onClick={closeModal}
          >
            <div className="modal-inner" onClick={(e) => e.stopPropagation()}>
              <div className="container">
                <div className="row text-end pb-3">
                  <div className="col">
                    <div className="close-btn" onClick={closeModal}>
                      Close <i className="fa fa-times"></i>
                    </div>
                  </div>
                </div>
                <div className="row justify-content-center py-5">
                  <div className="col-8 col-md-4 mb-4 pe-5">
                    <img
                      className="img-fluid"
                      src={`/img/portraits/portrait-${(
                        selectedSpeaker?.name ?? "n-a"
                      )
                        .toLowerCase()
                        .replace(/[^\w\s-]/g, "")
                        .replace(/\s+/g, "-")}.webp`}
                      alt={selectedSpeaker?.name ?? "N/A"}
                      onError={(e) => {
                        e.target.src = "/img/portraits/portrait-tba.webp";
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-8">
                    <h1 className="color-white mb-0">
                      {selectedSpeaker?.name ?? "N/A"}
                    </h1>
                    <strong className="color-white mb-5 d-block">
                      {selectedSpeaker?.role ?? "N/A"}
                    </strong>
                    <p
                      className="color-white"
                      dangerouslySetInnerHTML={{
                        __html: selectedSpeaker?.description ?? "N/A",
                      }}
                    />

                    {sessionData?.title && (
                      <div className="session-box">
                        <h2 className="session-box__title">
                          {sessionData.title}
                        </h2>
                        <p className="session-box__meta">
                          <strong>{sessionData.date}</strong>
                          {sessionData.date && sessionData.time && (
                            <span className="session-box__dot"> &bull; </span>
                          )}
                          {sessionData.time}
                        </p>
                        <p
                          className="session-box__description"
                          dangerouslySetInnerHTML={{
                            __html: sessionData.description ?? "",
                          }}
                        />
                        {/* <a href="/agenda" className="btn-white-o">
                            VIEW FULL AGENDA <i className="fa fa-arrow-right"></i>
                          </a> */}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          modalContainer,
        )}
    </>
  );
}

export function SpeakerCardSlider({ className, data }) {
  const settings = {
    infinite: true,
    centerMode: true,
    centerPadding: "0px",
    slidesToShow: 3,
    slidesToScroll: 3,
    dots: true,
    prevArrow: (
      <div className="slick-prev">
        <i className="fa-solid fa-arrow-left"></i>
      </div>
    ),
    nextArrow: (
      <div className="slick-next">
        <i className="fa-solid fa-arrow-right"></i>
      </div>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: true,
          centerPadding: "0px",
        },
      },
    ],
  };

  const flattenedData = data
    .map((speaker) =>
      speaker.ytData
        ? speaker.ytData
            .filter((presentation) => !presentation.hideFromSlider)
            .map((presentation) => ({
              ...presentation,
              speakerName: speaker.name,
              speakerRole: speaker.role,
            }))
        : [],
    )
    .flat();

  return (
    <Slider {...settings} className={`${className} presentation-slider`}>
      {Array.isArray(flattenedData) ? (
        flattenedData.map((card, index) => (
          <a
            key={index}
            href={`https://www.youtube.com/watch?v=${card.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="slide"
          >
            <img className="img-fluid" src={card.img} alt={card.title} />
          </a>
        ))
      ) : (
        <p>No speakers available</p>
      )}
    </Slider>
  );
}
